import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  NavLink as RouterLink,
  matchPath,
  useLocation,
} from "react-router-dom";
// material
import { alpha, useTheme, styled } from "@mui/material/styles";
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListItemButton,
} from "@mui/material";
import SportsIcon from "@mui/icons-material/Sports";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import CategoryIcon from "@mui/icons-material/Category";
import PaymentsIcon from "@mui/icons-material/Payments";
import SupportIcon from '@mui/icons-material/Support';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
//
import Iconify from "./Iconify";

// ----------------------------------------------------------------------

const ListItemStyle = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: "relative",
  textTransform: "capitalize",
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(2.5),
  color: theme.palette.text.secondary,
  "&:before": {
    top: 0,
    right: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: "none",
    position: "absolute",
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main,
  },
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
};

const getIcon = (name) => <Iconify icon={name} width={22} height={22} />;

function NavItem({ item, active }) {
  const theme = useTheme();
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children } = item;
  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const activeRootStyle = {
    color: "primary.main",
    fontWeight: "fontWeightMedium",
    bgcolor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity
    ),
    "&:before": { display: "block" },
  };

  const activeSubStyle = {
    color: "text.primary",
    fontWeight: "fontWeightMedium",
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle),
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
          <ListItemText disableTypography primary={title} />
          {info && info}
          <Iconify
            icon={
              open
                ? "eva:arrow-ios-downward-fill"
                : "eva:arrow-ios-forward-fill"
            }
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((item) => {
              const { title, path } = item;
              const isActiveSub = active(path);

              return (
                <ListItemStyle
                  key={title}
                  component={RouterLink}
                  to={path}
                  sx={{
                    ...(isActiveSub && activeSubStyle),
                  }}
                >
                  <ListItemIconStyle>
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 4,
                        display: "flex",
                        borderRadius: "50%",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "text.disabled",
                        transition: (theme) =>
                          theme.transitions.create("transform"),
                        ...(isActiveSub && {
                          transform: "scale(2)",
                          bgcolor: "primary.main",
                        }),
                      }}
                    />
                  </ListItemIconStyle>
                  <ListItemText disableTypography primary={title} />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle),
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      <ListItemText disableTypography primary={title} />
      {info && info}
    </ListItemStyle>
  );
}

export default function NavSection() {
  const { pathname } = useLocation();
  const match = (path) =>
    path ? !!matchPath({ path, end: false }, pathname) : false;

  const [showAdminBoard, setShowAdminBoard] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setShowAdminBoard(user.roles.includes("ROLE_ADMIN"));
    }
  }, []);

  const sidebarConfig = [
    ...(showAdminBoard ? [
      {
        title: "dashboard",
        path: "/dashboard/app",
        icon: getIcon("eva:pie-chart-2-fill"),
      },
      {
        title: "branches",
        path: "/dashboard/branches",
        icon: <CorporateFareIcon />,
      },
      {
        title: "members",
        path: "/dashboard/members",
        icon: getIcon("eva:people-fill"),
      },
      {
        title: "trainers",
        path: "/dashboard/trainers",
        icon: <SportsIcon />,
      },
      {
        title: 'attendance',
        path: '/dashboard/attendance',
        icon: getIcon("eva:people-fill"),
      },
      {
        title: "calendar",
        path: "/dashboard/callender",
        icon: getIcon("eva:calendar-fill"),
      },
      {
        title: "Sport Type",
        path: "/dashboard/sportType",
        icon: <CategoryIcon />,
      },
      {
        title: "Payment",
        path: "/dashboard/payment",
        icon: <PaymentsIcon />,
      },
      {
        title: "Subscription",
        path: "/dashboard/subscriptions",
        icon: <CardMembershipIcon />,
      },
      {
        title: "Enquiry",
        path: "/dashboard/enquiry",
        icon: <SupportIcon />,
      }
    ] : []),
    // member
    ...(!showAdminBoard ? [
      {
        title: 'attendance',
        path: '/dashboard/member/attendance',
        icon: getIcon("eva:people-fill"),
      },
    ] : [])

    // {
    //   title: "Products",
    //   path: "/dashboard/Products",
    //   icon: <ProductionQuantityLimitsIcon />,
    // },
    // {
    //   title: "blog",
    //   path: "/dashboard/blog",
    //   icon: getIcon("eva:file-text-fill"),
    // },
    // ...(showAdminBoard
    //   ? [
    //       {
    //         title: "register",
    //         path: "/",
    //         icon: getIcon("eva:person-add-fill"),
    //       },
    //     ]
    //   : []),
  ];

  return (
    <Box>
      <List disablePadding>
        {sidebarConfig.map((item) => (
          <NavItem key={item.title} item={item} active={match} />
        ))}
      </List>
    </Box>
  );
}
